import {
  startOfDay, endOfDay, startOfMonth, endOfMonth, subDays, subMonths, format, parse,
} from 'date-fns';

export const SALE_STATS_PRESETS = ['last30', 'thisMonth', 'lastMonth', 'last3Months', 'custom'];
export const DEFAULT_SALE_STATS_PRESET = 'last30';

// Returns an inclusive { from, to } Date pair for a named preset (local time).
// Not valid for 'custom' — the caller supplies custom dates directly.
export const presetToDates = (preset, now) => {
  switch (preset) {
    case 'last30':
      return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
    case 'thisMonth':
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case 'lastMonth': {
      const prev = subMonths(now, 1);
      return { from: startOfMonth(prev), to: endOfMonth(prev) };
    }
    case 'last3Months':
      return { from: startOfDay(subMonths(now, 3)), to: endOfDay(now) };
    default:
      throw new Error(`Unknown sale-stats preset: ${preset}`);
  }
};

// Snaps a from/to Date pair to inclusive local-day boundaries and returns
// explicit UTC ISO instants (never YYYY-MM-DD) for the query string.
export const toIsoRange = (fromDate, toDate) => ({
  from: startOfDay(fromDate).toISOString(),
  to: endOfDay(toDate).toISOString(),
});

export const isValidRange = (fromDate, toDate) =>
  fromDate instanceof Date &&
  toDate instanceof Date &&
  !Number.isNaN(fromDate.getTime()) &&
  !Number.isNaN(toDate.getTime()) &&
  fromDate.getTime() <= toDate.getTime();

// ---------------------------------------------------------------------------
// URL persistence
//
// The query string carries the *preset name*, not the resolved window:
// `?range=last30` is recomputed against "now" on every load, so a bookmarked
// relative preset never reopens as a stale frozen window. Only `custom` needs
// explicit dates, and those travel as human-readable YYYY-MM-DD — the UTC ISO
// instants the API expects are still produced by toIsoRange at the boundary.
// ---------------------------------------------------------------------------

const DAY_PARAM_FORMAT = 'yyyy-MM-dd';
const DAY_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

const parseDayParam = (value) => {
  if (!value || !DAY_PARAM_RE.test(value)) return null;
  const parsed = parse(value, DAY_PARAM_FORMAT, new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Reads a location search string into { preset, from, to } (Dates, day-snapped).
// Anything unrecognised — unknown preset, missing/malformed/reversed custom
// dates — degrades to the default preset rather than throwing or rendering an
// empty table, since these values are user-editable.
export const parseSaleStatsParams = (search, now = new Date()) => {
  const params = new URLSearchParams(search);
  const requested = params.get('range');
  const preset = SALE_STATS_PRESETS.includes(requested) ? requested : DEFAULT_SALE_STATS_PRESET;

  const fallback = () => ({
    preset: DEFAULT_SALE_STATS_PRESET,
    ...presetToDates(DEFAULT_SALE_STATS_PRESET, now),
  });

  if (preset !== 'custom') return { preset, ...presetToDates(preset, now) };

  const from = parseDayParam(params.get('from'));
  const to = parseDayParam(params.get('to'));
  if (!isValidRange(from, to)) return fallback();

  return { preset: 'custom', from: startOfDay(from), to: endOfDay(to) };
};

// Inverse of parseSaleStatsParams. Presets serialize to `range` alone so that
// switching away from custom cannot strand orphaned from/to params in the URL.
export const saleStatsParamsFor = (preset, fromDate, toDate) => {
  if (preset !== 'custom') return { range: preset };
  return {
    range: 'custom',
    from: format(fromDate, DAY_PARAM_FORMAT),
    to: format(toDate, DAY_PARAM_FORMAT),
  };
};
